import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-toggle',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <label
        [for]="id() || name()"
        [class]="toggleLabelClasses"
        [attr.aria-disabled]="disabled()">
        <input
          type="checkbox"
          [id]="id() || name()"
          [name]="name()"
          [checked]="checked()"
          [disabled]="disabled()"
          [class]="'sr-only'"
          (change)="onChange($event)"
          [attr.aria-checked]="checked()"
          [attr.aria-label]="label() || ariaLabel()" />
        <span [class]="toggleSwitchClasses">
          <span [class]="toggleThumbClasses"></span>
        </span>
        @if (showLabel()) {
          <span [class]="toggleTextClasses">
            {{ checked() ? onLabel() : offLabel() }}
          </span>
        }
      </label>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class ToggleComponent implements ControlValueAccessor {
  label = input<string>('');
  ariaLabel = input<string>('');
  checked = signal(false);
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  size = input<ComponentSize>('md');
  onLabel = input<string>('On');
  offLabel = input<string>('Off');
  showLabel = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');

  checkedChange = output<boolean>();
  touched = signal(false);

  private onChangeFn = (value: boolean) => {};
  private onTouchedFn = () => {};

  onChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.checked;
    this.checked.set(newValue);
    this.onChangeFn(newValue);
    this.checkedChange.emit(newValue);
    this.markAsTouched();
  }

  get toggleLabelClasses(): string {
    const classes = ['flex', 'items-center', 'gap-3', 'cursor-pointer'];
    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed');
    }
    return classes.join(' ');
  }

  get toggleSwitchClasses(): string {
    const sizeMap = {
      xs: 'w-8 h-4',
      sm: 'w-10 h-5',
      md: 'w-12 h-6',
      lg: 'w-14 h-7',
      xl: 'w-16 h-8'
    };

    const classes = [
      'relative',
      'inline-flex',
      'flex-shrink-0',
      'rounded-full',
      'transition-colors',
      'duration-200',
      'ease-in-out',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-2',
      sizeMap[this.size()]
    ];

    if (this.checked()) {
      classes.push('bg-blue-600', 'dark:bg-blue-500');
    } else {
      classes.push('bg-gray-200', 'dark:bg-gray-700');
    }

    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }

    return classes.join(' ');
  }

  get toggleThumbClasses(): string {
    const sizeMap = {
      xs: 'w-3 h-3 translate-x-0.5',
      sm: 'w-4 h-4 translate-x-0.5',
      md: 'w-5 h-5 translate-x-0.5',
      lg: 'w-6 h-6 translate-x-0.5',
      xl: 'w-7 h-7 translate-x-0.5'
    };

    const translateMap = {
      xs: 'translate-x-4',
      sm: 'translate-x-5',
      md: 'translate-x-6',
      lg: 'translate-x-7',
      xl: 'translate-x-8'
    };

    const classes = [
      'pointer-events-none',
      'inline-block',
      'rounded-full',
      'bg-white',
      'shadow-lg',
      'transform',
      'transition-transform',
      'duration-200',
      'ease-in-out',
      sizeMap[this.size()]
    ];

    if (this.checked()) {
      classes.push(translateMap[this.size()]);
    }

    return classes.join(' ');
  }

  get toggleTextClasses(): string {
    return 'text-sm font-medium text-gray-700 dark:text-gray-300';
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

